import React from 'react';
import styled from 'styled-components';

interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
  steps: string[];
}

const ProgressContainer = styled.div`
  margin: 15px 0 20px 0;
  
  @media (min-width: 768px) {
    margin: 30px 0 40px 0;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: #e0e0e0;
    z-index: 1;
  }
`;

const ProgressLine = styled.div<{ progress: number }>`
  position: absolute;
  top: 50%;
  left: 0;
  height: 2px;
  background: ${props => props.theme.primaryColor};
  z-index: 2;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 3;
  min-width: 60px;
  
  @media (min-width: 768px) {
    min-width: 120px;
  }
  text-align: center;
`;

const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
  transition: all 0.3s;
  
  ${props => {
    if (props.completed) {
      return `
        background: ${props.theme.primaryColor};
        color: white;
        border: 2px solid ${props.theme.primaryColor};
      `;
    } else if (props.active) {
      return `
        background: white;
        color: ${props.theme.primaryColor};
        border: 2px solid ${props.theme.primaryColor};
        box-shadow: 0 0 0 4px ${props.theme.primaryColor}1a;
      `;
    } else {
      return `
        background: white;
        color: #999;
        border: 2px solid #e0e0e0;
      `;
    }
  }}
`;

const StepLabel = styled.div<{ active: boolean; completed: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${props =>
    props.active || props.completed ? props.theme.primaryColor : '#999'
  };
  max-width: 100px;
  line-height: 1.3;
`;

const ProgressText = styled.div`
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const ProgressBar: React.FC<ProgressBarProps> = ({ 
  currentStep, 
  totalSteps, 
  steps 
}) => {
  const progress = (currentStep / (totalSteps - 1)) * 100;

  return (
    <ProgressContainer>
      <StepsContainer>
        <ProgressLine progress={progress} />
        {steps.map((step, index) => (
          <Step
            key={index}
            active={index === currentStep}
            completed={index < currentStep}
          >
            <StepCircle
              active={index === currentStep}
              completed={index < currentStep}
            >
              {index < currentStep ? '✓' : index + 1}
            </StepCircle>
            <StepLabel
              active={index === currentStep}
              completed={index < currentStep}
            >
              {step}
            </StepLabel>
          </Step>
        ))}
      </StepsContainer>
      <ProgressText>
        Step {currentStep + 1} of {totalSteps}: {steps[currentStep]}
      </ProgressText>
    </ProgressContainer>
  );
};

export default ProgressBar;